import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import {
  AccessibleOutlined,
  CalendarMonthOutlined,
  CurrencyRupeeOutlined,
  EditOutlined,
  HomeWorkOutlined,
  LocalHospitalOutlined,
  PlayArrowRounded,
  TagOutlined,
  TimerOutlined,
  TrendingUpRounded,
  TuneOutlined,
} from "@mui/icons-material";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel, PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { MilestoneStepper, PlainInput, RecordHeaderCard, SwitchLine, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { COVERAGE_COLORS, OVERRIDE_OPTIONS, formatDay, money } from "./esiEligibilityData";

// Employee ESI Details (E4 / E2): why the employee is or isn't covered and
// until when (the rule the server applied), their identifiers, open
// Validation alerts and coverage history. Edit ESI Details (E3): IP
// number, dispensary, disability and a coverage override, saved through
// PUT employees/<id>/ (the server recalculates coverage).

const { indigo, pink, blue, green, orange, purple } = detailAccentPalette;
const RED = "#DC2626";

const codeText = (code) => (code ? `${code.code_display || code.code} · ${code.branch_name || ""}`.replace(/ · $/, "") : "");
// Why there is no ESI code: no site set, or the site isn't in a code.
const noCodeText = (site) => (site ? `Site not linked — add ${site.name}${site.code ? ` (${site.code})` : ""} to an ESI code` : "No site — set it in the employee profile");

const metaOf = (row) => [row.emp_id, row.department, row.code_label].filter(Boolean).join(" · ");

const Alerts = ({ alerts }) =>
  alerts.length ? (
    <Mui.Stack spacing={1}>
      {alerts.map((alert) => (
        <RequestInfoBanner key={alert.id ?? alert.message} tone={alert.severity === "BLOCKING" ? "danger" : "warning"} body={alert.message} />
      ))}
    </Mui.Stack>
  ) : null;

export const EsiDetailsPanel = ({ open, loading, error, detail, onRetry, onClose, onEdit }) => {
  const row = detail?.row;
  const coverage = detail?.coverage;
  const identifiers = detail?.identifiers;
  const covered = coverage?.status === "COVERED";
  const joiner = coverage?.reason === "JOINER";

  const coverageFields = detail
    ? [
        { icon: CalendarMonthOutlined, label: "Contribution period", value: coverage.period?.label || "–", accent: indigo },
        {
          icon: CurrencyRupeeOutlined,
          label: joiner ? "Wage at joining" : "Wage at period start",
          value: coverage.wage_at_test === "" ? "–" : money(coverage.wage_at_test),
          helper: coverage.rule_applied || undefined,
          accent: green,
          multiline: true,
        },
        ...(coverage.current_wage !== "" && coverage.current_wage !== coverage.wage_at_test
          ? [{ icon: TrendingUpRounded, label: "Current wage", value: money(coverage.current_wage), accent: orange }]
          : []),
        covered
          ? {
              icon: PlayArrowRounded,
              label: "Covered from",
              value: formatDay(coverage.covered_from),
              helper: coverage.covered_from_rule || undefined,
              accent: green,
              multiline: true,
            }
          : { icon: PlayArrowRounded, label: "Not covered", value: "No ESI this period", helper: coverage.rule_applied || undefined, accent: purple, multiline: true },
        ...(covered
          ? [{ icon: TimerOutlined, label: "Covered until", value: formatDay(coverage.covered_until), helper: coverage.covered_until_rule || undefined, accent: pink }]
          : []),
        ...(coverage.notes ? [{ icon: CurrencyRupeeOutlined, label: "Contribution", value: coverage.notes, accent: orange, multiline: true }] : []),
      ]
    : [];

  const identifierFields = detail
    ? [
        identifiers.ip_number
          ? { icon: TagOutlined, label: "IP number", value: identifiers.ip_number, accent: purple }
          : {
              icon: TagOutlined,
              label: "IP number",
              value: <Mui.Box component="span" sx={{ color: covered ? RED : undefined }}>{covered ? "Missing" : "Not registered"}</Mui.Box>,
              helper: covered && identifiers.ip_due_on ? `Register within 10 days — due ${moment(identifiers.ip_due_on).format("DD MMM YYYY")}` : undefined,
              accent: pink,
            },
        ...(identifiers.site
          ? [{ icon: HomeWorkOutlined, label: "Site", value: `${identifiers.site.name}${identifiers.site.code ? ` (${identifiers.site.code})` : ""}`, accent: blue }]
          : []),
        { icon: HomeWorkOutlined, label: "ESI code", value: codeText(identifiers.esi_code) || noCodeText(identifiers.site), accent: blue },
        ...(identifiers.dispensary ? [{ icon: LocalHospitalOutlined, label: "Dispensary", value: identifiers.dispensary, accent: green }] : []),
        ...(identifiers.disability_visible
          ? [
              {
                icon: AccessibleOutlined,
                label: "Disability",
                value: identifiers.disability ? `Yes${identifiers.disability_from ? ` · from ${formatDay(identifiers.disability_from)}` : ""}` : "No",
                helper: identifiers.disability ? identifiers.disability_evidence_ref || undefined : undefined,
                accent: indigo,
              },
            ]
          : []),
      ]
    : [];

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Employee ESI Details"
      subtitle="Coverage, identifiers and history"
      bodySx={panelBodySx}
      // Edit is a header icon, like the Pay Inputs view panels.
      headerActions={
        <PanelHeaderIconButton title="Edit" onClick={onEdit} disabled={!detail} sx={{ color: designColors.tealDark }}>
          <EditOutlined />
        </PanelHeaderIconButton>
      }
    >
      {loading ? (
        <Mui.Box role="status" aria-label="Loading ESI details">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      ) : error ? (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load the ESI details."
          body={error}
          action={<SecondaryButton onClick={onRetry} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
        />
      ) : detail ? (
        <Mui.Stack spacing={2}>
          <RecordHeaderCard
            avatar={initialsOf(row.name)}
            title={row.name}
            chip={<RequestStatusChip status={coverage.label || row.coverage_label} colorCode={COVERAGE_COLORS[coverage.label || row.coverage_label]} />}
            meta={metaOf(row)}
            tone={covered ? (detail.alerts.some((alert) => alert.severity === "BLOCKING") || !identifiers.esi_code ? "danger" : "teal") : "slate"}
          />
          <Alerts alerts={detail.alerts} />
          {!identifiers.esi_code ? (
            <RequestInfoBanner
              tone="danger"
              title={identifiers.site ? "This employee's site is not in an ESI code." : "This employee has no site."}
              body={
                identifiers.site
                  ? "Coverage is unchanged, but this employee can't be in an ESIC file until the site is added to an ESI code (Configuration › Statutory › ESI › Employer Codes)."
                  : "Coverage is unchanged, but this employee can't be in an ESIC file until a site is set in the employee profile."
              }
            />
          ) : null}
          {coverage.locked ? (
            <RequestInfoBanner body="Coverage for this period is locked: payroll is finalised for part of the period. Corrections use a coverage override from the next open month." />
          ) : null}
          <RequestDetailsSection title="COVERAGE DETAILS" fields={coverageFields} />
          <RequestDetailsSection title="IDENTIFIERS" fields={identifierFields} />
          {detail.override ? (
            <RequestDetailsSection
              title="COVERAGE OVERRIDE"
              fields={[
                {
                  icon: TuneOutlined,
                  label: `${detail.override.status === "COVERED" ? "Covered" : "Not covered"} from ${formatDay(detail.override.from)}`,
                  value: detail.override.reason || "–",
                  helper:
                    detail.override.saved_by || detail.override.saved_at
                      ? `Saved${detail.override.saved_by ? ` by ${detail.override.saved_by}` : ""}${
                          detail.override.saved_at ? ` · ${moment(detail.override.saved_at).format("DD MMM YYYY, HH:mm")}` : ""
                        }`
                      : undefined,
                  accent: purple,
                  multiline: true,
                },
              ]}
            />
          ) : null}
          {detail.history.length ? <MilestoneStepper title="Coverage History" steps={detail.history} /> : null}
        </Mui.Stack>
      ) : null}
    </GlobalSidePanel>
  );
};

// The next period start after the shown period (default override date).
const nextPeriodStart = (period) => (period?.end ? moment(period.end).add(1, "day").format("YYYY-MM-DD") : moment().add(1, "month").startOf("month").format("YYYY-MM-DD"));

const formFor = (detail) => ({
  ip_number: detail.identifiers.ip_number || "",
  dispensary: detail.identifiers.dispensary || "",
  disability_on: detail.identifiers.disability,
  disability_from: detail.identifiers.disability_from || "",
  disability_evidence: detail.identifiers.disability_evidence_ref || "",
  override_status: detail.override?.status || "SYSTEM",
  override_from: detail.override?.from || nextPeriodStart(detail.coverage.period),
  override_reason: detail.override?.reason || "",
});

const validate = (form, covered, disabilityShown) => {
  const errors = {};
  if (form.ip_number && !/^\d{10}$/.test(form.ip_number)) errors.ip_number = "10 digits";
  else if (!form.ip_number && covered) errors.ip_number = "Enter the IP number from the ESIC portal";
  if (disabilityShown && form.disability_on) {
    if (!form.disability_from) errors.disability_from = "Pick the date it applies from";
    if (!String(form.disability_evidence || "").trim()) errors.disability_evidence = "Enter the certificate reference";
  }
  if (form.override_status !== "SYSTEM") {
    if (!form.override_from) errors.override_from = "Pick the 1st of a month";
    else if (moment(form.override_from).date() !== 1) errors.override_from = "Must be the 1st of a month";
    if (String(form.override_reason || "").trim().length < 5) errors.override_reason = "Give the reason (it is audited and shown in Validation)";
  }
  return errors;
};

// onSave(form) saves and closes; a rejected save keeps the panel open with
// the server's messages under the fields.
export const EsiEditPanel = ({ open, detail, onClose, onSave }) => {
  const [form, setForm] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !detail) return;
    setForm(formFor(detail));
    setShowErrors(false);
    setServerErrors({});
  }, [open, detail]);

  const covered = detail?.coverage.status === "COVERED";
  const disabilityShown = Boolean(detail?.identifiers.disability_visible);
  const errors = useMemo(() => (form ? validate(form, covered, disabilityShown) : {}), [form, covered, disabilityShown]);

  if (!detail || !form) return null;
  const { row, identifiers } = detail;
  const error = (key) => (showErrors ? errors[key] || serverErrors[key] : undefined);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const overriding = form.override_status !== "SYSTEM";
  const blocking = detail.alerts.filter((alert) => alert.severity === "BLOCKING");

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    setSaving(true);
    setServerErrors({});
    try {
      await onSave(form);
    } catch (err) {
      setServerErrors(err.fieldErrors || {});
      showToast("error", err.message || "Couldn't save the ESI details.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Edit ESI Details"
      subtitle={[row.name, row.emp_id].filter(Boolean).join(" · ")}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save" primaryProps={{ onClick: save, loading: saving }} />}
    >
      <Mui.Stack spacing={2}>
        {blocking.length ? <Alerts alerts={blocking} /> : null}

        <FormSection title="Identifiers" info="ESIC numbers used on the ESI returns and challans.">
          <Field wide label={<>IP number{covered ? <Required /> : null}</>} helper="10 digits, from the ESIC portal." error={error("ip_number")}>
            <TextField value={form.ip_number} onChange={(value) => set("ip_number")(value.replace(/\D/g, "").slice(0, 10))} error={error("ip_number")} placeholder="6312345683" />
          </Field>
          <Field label="ESI code" helper="Set by the employee's site.">
            <TextField value={codeText(identifiers.esi_code) || (identifiers.site ? "Site not linked" : "No site")} onChange={() => {}} disabled />
          </Field>
          <Field label="Dispensary" helper="ESI dispensary. Optional." error={error("dispensary")}>
            <TextField value={form.dispensary} onChange={set("dispensary")} placeholder="e.g. Erode" />
          </Field>
          {disabilityShown ? (
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              {/* Pull the switch back by its built-in 12px padding so it lines up with the fields. */}
              <Mui.Box sx={{ ml: -1.5 }}>
                <SwitchLine checked={form.disability_on} onChange={set("disability_on")} label="Person with disability" />
              </Mui.Box>
              <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>Uses the higher ESI wage ceiling from the next period start.</Mui.Typography>
              {form.disability_on ? (
                <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 1 }}>
                  <Field label={<>Applies from<Required /></>} helper="Date on the certificate." error={error("disability_from")}>
                    <DateField value={form.disability_from} onChange={set("disability_from")} error={error("disability_from")} />
                  </Field>
                  <Field label={<>Certificate reference<Required /></>} helper="Disability certificate number." error={error("disability_evidence")}>
                    <TextField value={form.disability_evidence} onChange={set("disability_evidence")} error={error("disability_evidence")} placeholder="e.g. certificate number" />
                  </Field>
                </Mui.Box>
              ) : null}
            </Mui.Box>
          ) : null}
        </FormSection>

        <FormSection title="Coverage Override" info="Only when the system result is wrong.">
          <Field label="Coverage" helper="Keep the system result or set one.">
            <SelectField value={form.override_status} onChange={(value) => value && set("override_status")(value)} options={OVERRIDE_OPTIONS} />
          </Field>
          <Field label={<>From{overriding ? <Required /> : null}</>} helper="1st of a month. Default: next period." error={error("override_from")}>
            <DateField value={form.override_from} onChange={set("override_from")} disabled={!overriding} error={error("override_from")} />
          </Field>
          <Field wide label={<>Reason{overriding ? <Required /> : null}</>} helper="Why the system result is wrong. At least 5 characters." error={error("override_reason")}>
            <PlainInput value={form.override_reason} onChange={set("override_reason")} disabled={!overriding} error={error("override_reason")} placeholder="e.g. Joined mid-period, covered from appointment" />
          </Field>
          <Mui.Typography sx={{ ...designType.captionText, gridColumn: "1 / -1", color: designColors.slate600 }}>
            Overrides need a reason. They are audited and listed in Validation (Info). Your company is responsible for the decision.
          </Mui.Typography>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
